<script setup>
import { onMounted, ref, watch, computed } from 'vue'
import { $api } from '@/utils/api'
import UserIdentity from '@/components/common/UserIdentity.vue'
import { useRoute, useRouter } from 'vue-router'

definePage({
  meta: {
    layout: 'default',
    requiresAuth: true,
    roles: ['admin'],
  },
})

const route = useRoute()
const router = useRouter()

const tabs = [
  { title: 'Approvals', value: 'approvals' },
  { title: 'Users', value: 'users' },
]

const activeTab = ref('approvals')

const normalizeQueryTab = value => Array.isArray(value) ? value[0] : value

const resolveTab = value => {
  const available = tabs.map(tab => tab.value)

  return available.includes(value) ? value : 'approvals'
}

const usersLoading = ref(false)
const usersError = ref('')
const usersTable = ref([])

const usersMeta = ref({
  total: 0,
  per_page: 10,
  current_page: 1,
})

const usersPagination = ref({
  page: 1,
  itemsPerPage: 10,
})

const usersFilters = ref({
  role: null,
  status: null,
  name: '',
  email: '',
})

const usersSearch = ref('')
const usersSortBy = ref([{ key: 'timestamps', order: 'desc' }])
const usersControlsDrawer = ref(false)

const userActionDialog = ref(false)
const userActionMode = ref('message')
const userActionTarget = ref(null)
const userActionReason = ref('')
const userActionLoading = ref(false)
const userActionError = ref('')

const statusConfirmDialog = ref(false)
const statusConfirmTarget = ref(null)
const statusConfirmMode = ref('suspend')
const statusConfirmLoading = ref(false)
const statusConfirmError = ref('')

const statusOptions = [
  { label: 'All statuses', value: null },
  { label: 'Pending approval', value: 'pending' },
  { label: 'Enabled', value: 'enabled' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Suspended', value: 'suspended' },
]

const roleOptions = [
  { label: 'All roles', value: null },
  { label: 'Surgeon secretaries', value: 'surgeon_secretary' },
  { label: 'Anaesthetist secretaries', value: 'secretary_anesthetist' },
  { label: 'Anaesthetists', value: 'anaesthetist' },
]

const subscriptionOptions = [
  { label: 'Standard', value: 'standard' },
  { label: 'Premium', value: 'premium' },
]

const resolveRole = item => item?.roles?.[0]?.name ?? item?.role ?? ''

const resolveIdentityName = item => {
  const person = item?.personal_data ?? item?.personalData ?? null
  if (person?.full_name) return person.full_name
  const first = person?.first_name
  const last = person?.last_name

  return [first, last].filter(Boolean).join(' ') || item?.name || '—'
}

const normalizePhoneHref = value => {
  if (!value) return null
  const normalized = String(value).replace(/[^\d+]/g, '')

  return normalized || null
}

const resolveTimestampLine = item => {
  const status = resolveApprovalStatus(item)
  const date = formatDate(item?.status_changed_at)
  if (status === 'enabled' || status === 'approved') return `Approved on: ${date}`
  if (status === 'rejected') return `Rejected on: ${date}`
  if (status === 'suspended') return `Suspended on: ${date}`

  return ''
}

const resolveTimestampDotColor = item => {
  const status = resolveApprovalStatus(item)
  if (status === 'enabled' || status === 'approved') return 'success'
  if (status === 'rejected') return 'error'
  if (status === 'suspended') return 'warning'

  return null
}

const resolvePhone = item => {
  const contacts = item?.userable?.contacts ?? []

  const normalized = contacts.map(contact => ({
    ...contact,
    type: String(contact?.type ?? '').toLowerCase(),
  }))

  const primary = normalized.find(contact => contact.is_primary && ['phone', 'mobile'].includes(contact.type))
  if (primary?.value)
    return primary.value

  const fallback = normalized.find(contact => ['phone', 'mobile'].includes(contact.type))

  return fallback?.value ?? null
}

const formatDate = value => {
  if (!value)
    return '—'

  try {
    return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium' }).format(new Date(value))
  } catch {
    return value
  }
}

const fetchUsers = async () => {
  usersLoading.value = true
  usersError.value = ''

  const activeSort = usersSortBy.value?.[0] ?? null

  const sortFieldMap = {
    identity: 'name',
    contacts: 'email',
    timestamps: 'created_at',
    status: 'status',
    subscription_tier: 'subscription_tier',
  }

  const sortBy = activeSort ? (sortFieldMap[activeSort.key] ?? activeSort.key) : undefined
  const sortDirection = activeSort?.order ?? undefined

  try {
    const response = await $api('/api/admin/users', {
      method: 'GET',
      query: {
        page: usersPagination.value.page,
        per_page: usersPagination.value.itemsPerPage,
        role: usersFilters.value.role || undefined,
        status: usersFilters.value.status || undefined,
        search: usersSearch.value?.trim() || undefined,
        name: usersFilters.value.name?.trim() || undefined,
        email: usersFilters.value.email?.trim() || undefined,
        sort_by: sortBy,
        sort_direction: sortDirection,
      },
    })

    const rows = response?.data?.data ?? response?.data ?? []
    const meta = response?.data?.meta ?? response?.meta ?? response?.data ?? {}

    usersTable.value = rows
    usersMeta.value = {
      total: meta.total ?? rows.length ?? 0,
      per_page: meta.per_page ?? usersPagination.value.itemsPerPage,
      current_page: meta.current_page ?? usersPagination.value.page,
    }
  } catch (err) {
    usersError.value = err?.response?._data?.message
      ?? err?.data?.message
      ?? err?.message
      ?? 'Unable to load users.'
  } finally {
    usersLoading.value = false
  }
}

const openUserAction = (mode, user) => {
  userActionMode.value = mode
  userActionTarget.value = user
  userActionReason.value = ''
  userActionError.value = ''
  userActionDialog.value = true
}

const closeUserAction = () => {
  userActionDialog.value = false
  userActionTarget.value = null
  userActionReason.value = ''
  userActionError.value = ''
}

const submitUserAction = async () => {
  if (!userActionTarget.value)
    return

  if (userActionMode.value === 'message' && !userActionReason.value.trim()) {
    userActionError.value = 'Please enter a message.'

    return
  }

  if (userActionMode.value === 'rejected' && !userActionReason.value.trim()) {
    userActionError.value = 'A reason is required.'

    return
  }

  userActionLoading.value = true
  userActionError.value = ''

  try {
    if (userActionMode.value === 'message') {
      await $api(`/api/admin/users/${userActionTarget.value.id}/message`, {
        method: 'POST',
        body: {
          message: userActionReason.value,
        },
      })
    } else {
      await $api(`/api/admin/users/${userActionTarget.value.id}/status`, {
        method: 'PATCH',
        body: {
          status: userActionMode.value,
          reason: userActionReason.value || null,
        },
      })
    }

    closeUserAction()
    await fetchUsers()
  } catch (err) {
    userActionError.value = err?.response?._data?.message
      ?? err?.data?.message
      ?? err?.message
      ?? 'Unable to complete the action.'
  } finally {
    userActionLoading.value = false
  }
}

const updateUsersTableUser = (userId, updates) => {
  const index = usersTable.value.findIndex(item => item.id === userId)
  if (index === -1)
    return

  usersTable.value[index] = {
    ...usersTable.value[index],
    ...updates,
  }
}

const applyStatusUpdate = async (item, status, reason = null) => {
  await $api(`/api/admin/users/${item.id}/status`, {
    method: 'PATCH',
    body: {
      status,
      reason,
    },
  })

  const statusChangedAt = new Date().toISOString()

  updateUsersTableUser(item.id, {
    status,
    status_reason: reason,
    status_changed_at: statusChangedAt,
  })
}

const approvalStatusLabel = status => {
  if (status === 'approved' || status === 'enabled')
    return 'Approved'
  if (status === 'rejected')
    return 'Rejected'
  if (status === 'suspended')
    return 'Suspended'
  if (status === 'pending')
    return 'Pending Approval'

  return 'Pending Approval'
}

const approvalStatusColor = status => {
  if (status === 'approved' || status === 'enabled')
    return 'success'
  if (status === 'rejected')
    return 'error'
  if (status === 'suspended')
    return 'error'
  if (status === 'pending')
    return 'warning'

  return 'warning'
}

const resolveApprovalStatus = item => item?.admin_status ?? item?.status ?? 'pending'

const openStatusConfirmDialog = (mode, item) => {
  statusConfirmMode.value = mode
  statusConfirmTarget.value = item
  statusConfirmLoading.value = false
  statusConfirmError.value = ''
  statusConfirmDialog.value = true
}

const closeStatusConfirmDialog = () => {
  statusConfirmDialog.value = false
  statusConfirmTarget.value = null
  statusConfirmMode.value = 'suspend'
  statusConfirmLoading.value = false
  statusConfirmError.value = ''
}

const submitStatusConfirmation = async () => {
  if (!statusConfirmTarget.value)
    return

  statusConfirmLoading.value = true
  statusConfirmError.value = ''

  try {
    const nextStatus = statusConfirmMode.value === 'activate' ? 'enabled' : 'suspended'

    await applyStatusUpdate(statusConfirmTarget.value, nextStatus, null)
    closeStatusConfirmDialog()
    await fetchUsers()
  } catch (err) {
    statusConfirmError.value = err?.response?._data?.message
      ?? err?.data?.message
      ?? err?.message
      ?? 'Unable to update status.'
  } finally {
    statusConfirmLoading.value = false
  }
}

const resolveStatusActions = item => {
  const status = resolveApprovalStatus(item)

  if (status === 'pending') {
    return [
      { key: 'approve', label: 'Approve user', icon: 'ri-checkbox-circle-line', color: 'success', handler: () => openStatusConfirmDialog('activate', item) },
      { key: 'reject', label: 'Reject user', icon: 'ri-close-circle-line', color: 'error', handler: () => openUserAction('rejected', item) },
      { key: 'suspend', label: 'Suspend user', icon: 'ri-pause-circle-line', color: 'warning', handler: () => openStatusConfirmDialog('suspend', item) },
    ]
  }

  if (status === 'enabled' || status === 'approved') {
    return [
      { key: 'suspend', label: 'Suspend user', icon: 'ri-pause-circle-line', color: 'warning', handler: () => openStatusConfirmDialog('suspend', item) },
      { key: 'disable', label: 'Disable account', icon: 'ri-forbid-line', color: 'error', handler: () => openUserAction('rejected', item) },
    ]
  }

  if (status === 'suspended') {
    return [
      { key: 'reactivate', label: 'Reactivate user', icon: 'ri-play-circle-line', color: 'success', handler: () => openStatusConfirmDialog('activate', item) },
      { key: 'disable', label: 'Disable account', icon: 'ri-forbid-line', color: 'error', handler: () => openUserAction('rejected', item) },
    ]
  }

  if (status === 'rejected') {
    return [
      { key: 'reactivate', label: 'Reactivate user', icon: 'ri-play-circle-line', color: 'success', handler: () => openStatusConfirmDialog('activate', item) },
    ]
  }

  return []
}

const userHeaders = [
  { title: 'Identity', key: 'identity', sortable: true },
  { title: 'Contacts', key: 'contacts', sortable: false },
  { title: 'Timestamps', key: 'timestamps', sortable: true },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Tier', key: 'subscription_tier', sortable: true },
]


onMounted(() => {
  const tab = resolveTab(normalizeQueryTab(route.query.tab))

  activeTab.value = tab
  usersFilters.value.status = tab === 'approvals' ? 'pending' : null
  fetchUsers()
})

watch(() => route.query.tab, value => {
  const tab = resolveTab(normalizeQueryTab(value))

  activeTab.value = tab
  usersFilters.value.role = null
  usersFilters.value.status = tab === 'approvals' ? 'pending' : null
  usersPagination.value.page = 1
  fetchUsers()
})

watch([() => usersPagination.value.page, () => usersPagination.value.itemsPerPage], () => {
  fetchUsers()
})

watch(usersFilters, () => {
  usersPagination.value.page = 1
  fetchUsers()
}, { deep: true })

let usersSearchDebounce = null
watch(usersSearch, () => {
  usersPagination.value.page = 1
  if (usersSearchDebounce)
    clearTimeout(usersSearchDebounce)

  usersSearchDebounce = setTimeout(() => {
    fetchUsers()
  }, 350)
})

watch(usersSortBy, () => {
  usersPagination.value.page = 1
  fetchUsers()
}, { deep: true })
</script>

<template>
  <VContainer class="py-4">
    <VTabs
      v-model="activeTab"
      class="mb-4"
      @update:model-value="val => router.replace({ query: { ...route.query, tab: val } })"
    >
      <VTab
        v-for="tab in tabs"
        :key="tab.value"
        :value="tab.value"
      >
        {{ tab.title }}
      </VTab>
    </VTabs>

    <VWindow
      v-model="activeTab"
      class="disable-tab-transition"
      :touch="false"
    >
      <VWindowItem value="approvals">
        <VCard class="mb-4">
          <VCardTitle class="d-flex align-center justify-space-between">
            <span>Pending approvals</span>
            <VBtn
              variant="text"
              prepend-icon="ri-refresh-line"
              @click="fetchUsers"
            >
              Refresh
            </VBtn>
          </VCardTitle>
          <VDivider />
          <VCardText>
            <VRow>
              <VCol
                cols="12"
                md="6"
              >
                <VSelect
                  v-model="usersFilters.role"
                  :items="roleOptions"
                  item-title="label"
                  item-value="value"
                  label="Role"
                  variant="outlined"
                />
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VWindowItem>

      <VWindowItem value="users">
        <VCard class="mb-4">
          <VCardTitle class="d-flex align-center justify-space-between">
            <span>Users</span>
            <VBtn
              variant="text"
              prepend-icon="ri-refresh-line"
              @click="fetchUsers"
            >
              Refresh
            </VBtn>
          </VCardTitle>
          <VDivider />
          <VCardText>
            <VRow>
              <VCol
                cols="12"
                md="6"
              >
                <VSelect
                  v-model="usersFilters.role"
                  :items="roleOptions"
                  item-title="label"
                  item-value="value"
                  label="Role"
                  variant="outlined"
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <VSelect
                  v-model="usersFilters.status"
                  :items="statusOptions"
                  item-title="label"
                  item-value="value"
                  label="Status"
                  variant="outlined"
                />
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VWindowItem>
    </VWindow>

    <!-- Tabella condivisa tra Approvals e Users -->
    <VCard class="mb-4">
      <VCardText>
        <div class="d-flex flex-wrap align-center ga-3 mb-4">
          <VTextField
            v-model="usersSearch"
            label="Search users"
            placeholder="Search by name, email, role..."
            density="comfortable"
            variant="outlined"
            prepend-inner-icon="ri-search-line"
            clearable
            class="users-search-field"
          />
          <VBtn
            variant="outlined"
            color="primary"
            prepend-icon="ri-equalizer-line"
            @click="usersControlsDrawer = true"
          >
            Table controls
          </VBtn>
        </div>

        <VAlert
          v-if="usersError"
          type="error"
          variant="tonal"
          class="mb-4"
        >
          {{ usersError }}
        </VAlert>

        <VDataTableServer
          :headers="userHeaders"
          :items="usersTable"
          :items-length="usersMeta.total"
          :loading="usersLoading"
          :page="usersPagination.page"
          :items-per-page="usersPagination.itemsPerPage"
          :sort-by="usersSortBy"
          item-key="id"
          class="text-no-wrap users-table"
          @update:page="value => { usersPagination.page = value }"
          @update:items-per-page="value => { usersPagination.itemsPerPage = value }"
          @update:sort-by="value => { usersSortBy = value }"
        >
          <template #item.identity="{ item }">
            <div class="py-1">
              <UserIdentity
                :name="resolveIdentityName(item)"
                :subtitle="resolveRole(item)"
                :seed="String(item?.id ?? '')"
                :size="36"
              />
            </div>
          </template>

          <template #item.contacts="{ item }">
            <div class="d-flex flex-column ga-1 py-1">
              <a
                v-if="item?.email"
                class="contact-link text-body-2"
                :href="`mailto:${item.email}`"
              >
                <VIcon
                  size="14"
                  color="primary"
                  class="me-1"
                >ri-mail-line</VIcon>
                {{ item.email }}
              </a>
              <a
                v-if="resolvePhone(item)"
                class="contact-link text-body-2"
                :href="`tel:${normalizePhoneHref(resolvePhone(item))}`"
              >
                <VIcon
                  size="14"
                  color="primary"
                  class="me-1"
                >ri-phone-line</VIcon>
                {{ resolvePhone(item) }}
              </a>
            </div>
          </template>

          <template #item.timestamps="{ item }">
            <div class="d-flex flex-column ga-1 py-1">
              <span class="text-body-2 d-inline-flex align-center ga-1">
                <VIcon
                  size="14"
                  color="medium-emphasis"
                >
                  ri-calendar-line
                </VIcon>
                Joined on: {{ formatDate(item?.created_at) }}
              </span>
              <span
                v-if="resolveTimestampLine(item)"
                class="text-caption text-medium-emphasis d-inline-flex align-center ga-1"
              >
                <VIcon
                  v-if="resolveTimestampDotColor(item)"
                  :color="resolveTimestampDotColor(item)"
                  size="x-small"
                >
                  ri-circle-fill
                </VIcon>
                {{ resolveTimestampLine(item) }}
              </span>
            </div>
          </template>

          <template #item.status="{ item }">
            <VMenu
              v-if="resolveStatusActions(item).length"
              location="bottom start"
            >
              <template #activator="{ props }">
                <VBtnGroup
                  v-bind="props"
                  :color="approvalStatusColor(resolveApprovalStatus(item))"
                  variant="tonal"
                  density="compact"
                  divided
                  class="status-split-btn"
                >
                  <VBtn size="small">
                    {{ approvalStatusLabel(resolveApprovalStatus(item)) }}
                  </VBtn>
                  <VBtn
                    size="small"
                    width="28"
                    min-width="28"
                  >
                    <VIcon size="16">
                      ri-arrow-down-s-fill
                    </VIcon>
                  </VBtn>
                </VBtnGroup>
              </template>
              <VList
                density="compact"
                min-width="210"
              >
                <VListItem
                  v-for="action in resolveStatusActions(item)"
                  :key="action.key"
                  :title="action.label"
                  :prepend-icon="action.icon"
                  :base-color="action.color"
                  @click="action.handler()"
                />
              </VList>
            </VMenu>
            <VChip
              v-else
              :color="approvalStatusColor(resolveApprovalStatus(item))"
              size="small"
              variant="tonal"
              label
            >
              {{ approvalStatusLabel(resolveApprovalStatus(item)) }}
            </VChip>
          </template>

          <template #item.subscription_tier="{ item }">
            {{ item?.subscription_tier ?? 'standard' }}
          </template>
        </VDataTableServer>
      </VCardText>
    </VCard>

    <VNavigationDrawer
      v-model="usersControlsDrawer"
      location="right"
      temporary
      width="420"
    >
      <div class="pa-4">
        <div class="d-flex align-center justify-space-between mb-2">
          <h3 class="text-h6 mb-0">
            Table Controls
          </h3>
          <VBtn
            icon
            variant="text"
            @click="usersControlsDrawer = false"
          >
            <VIcon>ri-close-line</VIcon>
          </VBtn>
        </div>

        <p class="text-body-2 text-medium-emphasis mb-4">
          Configure filters, sorting and visible columns.
        </p>

        <VTextField
          v-model="usersFilters.name"
          label="Filter by name"
          variant="outlined"
          clearable
          class="mb-3"
        />

        <VTextField
          v-model="usersFilters.email"
          label="Filter by email"
          variant="outlined"
          clearable
          class="mb-3"
        />

        <VSelect
          v-model="usersSortBy"
          label="Sort by"
          variant="outlined"
          :items="[
            { title: 'Newest first', value: [{ key: 'timestamps', order: 'desc' }] },
            { title: 'Oldest first', value: [{ key: 'timestamps', order: 'asc' }] },
            { title: 'Name A-Z', value: [{ key: 'identity', order: 'asc' }] },
            { title: 'Name Z-A', value: [{ key: 'identity', order: 'desc' }] },
            { title: 'Status A-Z', value: [{ key: 'status', order: 'asc' }] },
            { title: 'Tier A-Z', value: [{ key: 'subscription_tier', order: 'asc' }] },
          ]"
          item-title="title"
          item-value="value"
          class="mb-3"
        />

        <div class="d-flex justify-end ga-2">
          <VBtn
            variant="text"
            @click="() => {
              usersFilters.name = ''
              usersFilters.email = ''
              usersSearch = ''
              usersSortBy = [{ key: 'timestamps', order: 'desc' }]
            }"
          >
            Reset
          </VBtn>
          <VBtn
            color="primary"
            @click="usersControlsDrawer = false"
          >
            Done
          </VBtn>
        </div>
      </div>
    </VNavigationDrawer>

    <VDialog
      v-model="userActionDialog"
      width="520"
    >
      <VCard>
        <VCardTitle class="text-h6">
          <span v-if="userActionMode === 'message'">Send a message</span>
          <span v-else-if="userActionMode === 'enabled'">Approve registration</span>
          <span v-else-if="userActionMode === 'rejected'">Reject / Disable account</span>
          <span v-else-if="userActionMode === 'suspended'">Suspend account</span>
          <span v-else>Change status</span>
        </VCardTitle>
        <VDivider />
        <VCardText>
          <div class="text-body-2 mb-4">
            {{ userActionTarget?.name }} · {{ userActionTarget?.email }}
          </div>
          <VSelect
            v-if="userActionMode === 'enabled'"
            v-model="userActionTarget.subscription_tier"
            :items="subscriptionOptions"
            item-title="label"
            item-value="value"
            label="Subscription tier"
            variant="outlined"
            disabled
          />
          <VTextarea
            v-model="userActionReason"
            label="Message"
            variant="outlined"
            rows="4"
            placeholder="Add a note about the decision or missing information."
          />
          <VAlert
            v-if="userActionError"
            type="error"
            variant="tonal"
            class="mt-4"
          >
            {{ userActionError }}
          </VAlert>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="closeUserAction"
          >
            Cancel
          </VBtn>
          <VBtn
            color="primary"
            :loading="userActionLoading"
            @click="submitUserAction"
          >
            Confirm
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="statusConfirmDialog"
      width="560"
    >
      <VCard>
        <VCardTitle class="text-h6">
          {{ statusConfirmMode === 'activate' ? 'Activate User' : 'Suspend User' }}
        </VCardTitle>
        <VDivider />
        <VCardText>
          <p
            v-if="statusConfirmMode === 'activate'"
            class="text-body-2"
          >
            Are you sure you want to activate {{ statusConfirmTarget?.name }}?
          </p>
          <p
            v-else
            class="text-body-2"
          >
            Are you sure you want to suspend {{ statusConfirmTarget?.name }}? This user will lose access immediately.
          </p>
          <VAlert
            v-if="statusConfirmError"
            type="error"
            variant="tonal"
            class="mt-4"
          >
            {{ statusConfirmError }}
          </VAlert>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="closeStatusConfirmDialog"
          >
            Cancel
          </VBtn>
          <VBtn
            :color="statusConfirmMode === 'activate' ? 'success' : 'error'"
            :loading="statusConfirmLoading"
            @click="submitStatusConfirmation"
          >
            Confirm
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </VContainer>
</template>

<style scoped>
.contact-link {
  display: flex;
  align-items: center;
  min-block-size: 24px;
  color: inherit;
  text-decoration: none;
}

.contact-link:hover {
  text-decoration: underline;
}

.status-split-btn :deep(.v-btn-group__border) {
  opacity: 0.4;
}

.status-split-btn :deep(.v-btn:last-child) {
  opacity: 0.85;
}

.status-split-btn:hover :deep(.v-btn:last-child) {
  opacity: 1;
}

.users-search-field {
  flex: 1 1 380px;
  min-inline-size: 260px;
}

/* eslint-disable-next-line */
.users-table :deep(.v-table__wrapper > table > tbody > tr > td) {
  vertical-align: top;
}
</style>
